import { createContext, useContext, useState, ReactNode, useCallback } from 'react';

type TranscriptGenerationContextType = {
    generatingTranscripts: Set<number>;
    generatingSummaries: Set<number>;
    startTranscriptGeneration: (phoneCallId: number) => void;
    stopTranscriptGeneration: (phoneCallId: number) => void;
    startSummaryGeneration: (phoneCallId: number) => void;
    stopSummaryGeneration: (phoneCallId: number) => void;
};

const TranscriptGenerationContext = createContext<TranscriptGenerationContextType | undefined>(
    undefined,
);

export const TranscriptGenerationProvider = ({ children }: { children: ReactNode }) => {
    const [generatingTranscripts, setGeneratingTranscripts] = useState<Set<number>>(new Set());
    const [generatingSummaries, setGeneratingSummaries] = useState<Set<number>>(new Set());

    const startTranscriptGeneration = useCallback((phoneCallId: number) => {
        setGeneratingTranscripts((prev) => new Set(prev).add(phoneCallId));
    }, []);

    const stopTranscriptGeneration = useCallback((phoneCallId: number) => {
        setGeneratingTranscripts((prev) => {
            const next = new Set(prev);
            next.delete(phoneCallId);
            return next;
        });
    }, []);

    const startSummaryGeneration = useCallback((phoneCallId: number) => {
        setGeneratingSummaries((prev) => new Set(prev).add(phoneCallId));
    }, []);

    const stopSummaryGeneration = useCallback((phoneCallId: number) => {
        setGeneratingSummaries((prev) => {
            const next = new Set(prev);
            next.delete(phoneCallId);
            return next;
        });
    }, []);

    return (
        <TranscriptGenerationContext.Provider
            value={{
                generatingTranscripts,
                generatingSummaries,
                startTranscriptGeneration,
                stopTranscriptGeneration,
                startSummaryGeneration,
                stopSummaryGeneration,
            }}
        >
            {children}
        </TranscriptGenerationContext.Provider>
    );
};

export const useTranscriptGeneration = () => {
    const context = useContext(TranscriptGenerationContext);
    if (context === undefined) {
        throw new Error(
            'useTranscriptGeneration must be used within a TranscriptGenerationProvider',
        );
    }
    return context;
};
